import { useEffect, useMemo, useState } from 'react'
import { AdminLayout, useAdminAccess } from '../../components/AdminLayout'
import { useLanguage } from '../../contexts/LanguageContext'
import { requestApi } from '../../lib/adminApi'

type SystemSetting = {
  key: string
  value?: string | null
  type: string
  description?: string | null
  group: string
  updated_at?: string | null
}

type SettingsResponse = {
  settings: SystemSetting[]
}

const AdminSettingsContent = () => {
  const { t } = useLanguage()
  const { isGranted } = useAdminAccess()
  const [settings, setSettings] = useState<SystemSetting[]>([])
  const [drafts, setDrafts] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')

  const fetchSettings = () => {
    setLoading(true)
    setError('')
    requestApi<SettingsResponse>('/api/v1/admin/settings')
      .then((payload) => {
        setSettings(payload.settings || [])
        const nextDrafts: Record<string, string> = {}
        payload.settings?.forEach((item) => {
          nextDrafts[item.key] = item.value ?? ''
        })
        setDrafts(nextDrafts)
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : t('admin.settings.error'))
      })
      .finally(() => {
        setLoading(false)
      })
  }

  useEffect(() => {
    if (!isGranted) return
    fetchSettings()
  }, [isGranted])

  const groupedSettings = useMemo(() => {
    const groups: Record<string, SystemSetting[]> = {}
    settings.forEach((item) => {
      const key = item.group || 'other'
      if (!groups[key]) {
        groups[key] = []
      }
      groups[key].push(item)
    })
    return groups
  }, [settings])

  const handleSave = async (setting: SystemSetting) => {
    const value = drafts[setting.key] ?? ''
    setMessage('')
    try {
      await requestApi('/api/v1/admin/settings', {
        method: 'PUT',
        body: JSON.stringify({
          key: setting.key,
          value
        })
      })
      setMessage(t('admin.settings.saved'))
      fetchSettings()
    } catch (err) {
      setMessage(err instanceof Error ? err.message : t('admin.settings.error'))
    }
  }

  const formatUpdatedAt = (value?: string | null) => {
    if (!value) return '-'
    try {
      return new Date(value).toLocaleString()
    } catch (error) {
      return value
    }
  }

  return (
    <div className="space-y-6">
        {loading && <p className="text-sm text-slate-400">{t('admin.settings.loading')}</p>}
        {error && <p className="text-sm text-rose-500">{error}</p>}
        {message && <p className="text-sm text-rose-500">{message}</p>}
        {!loading && !error && settings.length === 0 && (
          <p className="text-sm text-slate-400">{t('admin.settings.empty')}</p>
        )}

        {!loading && !error && settings.length > 0 && (
          <div className="space-y-6">
            {Object.entries(groupedSettings).map(([group, items]) => (
              <div key={group} className="border border-slate-200 rounded-2xl p-4">
                <h3 className="text-sm font-semibold text-slate-700 mb-4">
                  {t(`admin.settings.groups.${group}`)}
                </h3>
                <div className="space-y-4">
                  {items.map((setting) => {
                    const currentValue = drafts[setting.key] ?? ''
                    const isChanged = currentValue !== (setting.value ?? '')
                    const isBoolean = setting.type === 'boolean'
                    const isNumber = setting.type === 'number'
                    return (
                      <div key={setting.key} className="border border-slate-100 rounded-xl p-3">
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                          <div>
                            <p className="text-sm font-semibold text-slate-700">{setting.key}</p>
                            <p className="text-xs text-slate-400">{setting.description || '-'}</p>
                          </div>
                          <div className="flex flex-col md:flex-row md:items-center gap-3">
                            {isBoolean ? (
                              <label className="flex items-center gap-2 text-sm text-slate-600">
                                <input
                                  type="checkbox"
                                  checked={currentValue === 'true' || currentValue === '1'}
                                  onChange={(e) =>
                                    setDrafts((prev) => ({ ...prev, [setting.key]: e.target.checked ? 'true' : 'false' }))
                                  }
                                />
                                {t('admin.settings.boolean')}
                              </label>
                            ) : (
                              <input
                                type={isNumber ? 'number' : 'text'}
                                className="border border-slate-200 rounded-lg px-3 py-2 text-sm"
                                value={currentValue}
                                onChange={(e) => setDrafts((prev) => ({ ...prev, [setting.key]: e.target.value }))}
                              />
                            )}
                            <button
                              onClick={() => handleSave(setting)}
                              disabled={!isChanged}
                              className="px-3 py-2 text-xs font-semibold border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                            >
                              {t('admin.settings.save')}
                            </button>
                          </div>
                        </div>
                        <p className="text-[11px] text-slate-400 mt-2">
                          {t('admin.settings.updatedAt').replace('{time}', formatUpdatedAt(setting.updated_at))}
                        </p>
                      </div>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
  )
}

export const AdminSettingsScreen = () => {
  const { t } = useLanguage()

  return (
    <AdminLayout title={t('admin.settings.title')} description={t('admin.settings.desc')}>
      <AdminSettingsContent />
    </AdminLayout>
  )
}
